import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { QueryClientProvider } from "react-query";
import { queryClient } from "@/lib/queryClient";
import { ReactQueryDevtools } from "react-query/devtools";
import { Route, Routes } from "react-router";
import { usePendingWorkflowsListener } from "@/hooks/usePendingWorkflowsListener";
import { usePendingProductWorkflowsSocketListener } from "@/hooks/usePendingProductWorkflowsSocketListener";

// Eager: el layout envuelve cada ruta autenticada y el login es el primer paint
// del usuario no autenticado. Lazy-loadear cualquiera de los dos solo agrega
// un flash de spinner sin ahorrar nada útil.
import ClientLayout from "@/components/layout/ClientLayout";
import { RESOURCES, CLINICAL_RESOURCES } from "@/context/auth/resources";
import { RECURSO_PARA_ASIGNAR } from "@/context/auth/recursosParaAsignar";
import LoginOne from "@/pages/login/LoginOne";
import ActAsPage from "@/pages/act-as/ActAsPage";

// Pages — lazy. Cada uno genera su propio chunk al build y se descarga la
// primera vez que el usuario navega a esa ruta.
const OAuthCallbackPage = lazy(
  () => import("@/pages/oauth-callback/OAuthCallbackPage")
);

const SetPasswordPage = lazy(
  () => import("@/pages/set-password/SetPasswordPage")
);
const MiTiendaOnlinePage = lazy(
  () => import("@/pages/mi-tienda-online/MiTiendaOnlinePage")
);
const CreateStorePage = lazy(
  () => import("@/pages/mi-tienda-online/CreateStorePage")
);
const StoreDashboardPage = lazy(
  () => import("@/pages/mi-tienda-online/StoreDashboardPage")
);
const AparienciaPage = lazy(
  () => import("@/pages/mi-tienda-online/AparienciaPage")
);
const InformacionPage = lazy(
  () => import("@/pages/mi-tienda-online/InformacionPage")
);
const EnviosPage = lazy(() => import("@/pages/mi-tienda-online/EnviosPage"));
const PagosPage = lazy(() => import("@/pages/mi-tienda-online/PagosPage"));
const NotFoundPage = lazy(() => import("@/components/404/404"));
const BoardsPage = lazy(() => import("@/pages/boards/BoardsPage"));
const BoardPage = lazy(() => import("@/pages/boards/[id]/BoardPage"));
const BuyersPage = lazy(() => import("@/pages/buyers/BuyersPage"));
const AddBuyerPage = lazy(() => import("@/pages/buyers/add/AddBuyerPage"));
const DebtorsPage = lazy(() => import("@/pages/buyers/debtors/DebtorsPage"));
const EditBuyerPage = lazy(() => import("@/pages/buyers/edit/EditBuyerPage"));
const InitialDebtPage = lazy(
  () => import("@/pages/buyers/initial-debt/InitialDebtPage")
);
const AddInitialDebtPage = lazy(
  () => import("@/pages/buyers/initial-debt/add/AddInitialDebtPage")
);
const ProductGroupsPage = lazy(
  () => import("@/pages/product-groups/ProductGroupsPage")
);
const AddProductGroupPage = lazy(
  () => import("@/pages/product-groups/add/AddProductGroupPage")
);
const EditProductGroupPage = lazy(
  () => import("@/pages/product-groups/edit/EditProductGroupPage")
);
const ConsumptionGroupsPage = lazy(
  () => import("@/pages/consumption-groups/ConsumptionGroupsPage")
);
const AddConsumptionGroupPage = lazy(
  () => import("@/pages/consumption-groups/add/AddConsumptionGroupPage")
);
const EditConsumptionGroupPage = lazy(
  () => import("@/pages/consumption-groups/edit/EditConsumptionGroupPage")
);
const RepurchaseRemindersPage = lazy(
  () => import("@/pages/repurchase-reminders/RepurchaseRemindersPage")
);
const CategoriesPage = lazy(() => import("@/pages/categories/CategoriesPage"));
const AddCategoryPage = lazy(
  () => import("@/pages/categories/add/AddCategoryPage")
);
const AddSubcategoryPage = lazy(
  () => import("@/pages/categories/add/AddSubcategoryPage")
);
const EditCategoryPage = lazy(
  () => import("@/pages/categories/edit/EditCategoryPage")
);
const DisbursementsPage = lazy(
  () => import("@/pages/disbursements/DisbursementsPage")
);
const AddDisbursementPage = lazy(
  () => import("@/pages/disbursements/add/AddDisbursementPage")
);
const AssignDisbursementPage = lazy(
  () => import("@/pages/disbursements/assign/AssignDisbursementPage")
);
const InitialPurchasesPage = lazy(
  () => import("@/pages/initial-purchases/InitialPurchasesPage")
);
const AddInitialPurchasePage = lazy(
  () => import("@/pages/initial-purchases/add/AddInitialPurchasePage")
);
const InventoryValuationPage = lazy(
  () => import("@/pages/inventory-valuation/InventoryValuationPage")
);
const InventoryValuationDetailsPage = lazy(
  () =>
    import(
      "@/pages/inventory-valuation/[warehouseId]/InventoryValuationDetailsPage"
    )
);
const InventoryPage = lazy(() => import("@/pages/inventory/InventoryPage"));
const WarehouseInventoryPage = lazy(
  () => import("@/pages/inventory/[warehouseId]/WarehouseInventoryPage")
);
const RetailListsPage = lazy(
  () => import("@/pages/lists-retail/RetailListsPage")
);
const AddRetailListPage = lazy(
  () => import("@/pages/lists-retail/add/AddRetailListPage")
);
const EditRetailListPage = lazy(
  () => import("@/pages/lists-retail/edit/EditRetailListPage")
);
const LoosePage = lazy(() => import("@/pages/loose/LoosePage"));
const LooseByWarehousePage = lazy(
  () => import("@/pages/loose/[warehouseId]/LooseByWarehousePage")
);
const AddLoosePage = lazy(
  () => import("@/pages/loose/[warehouseId]/add/AddLoosePage")
);
const MinStockPage = lazy(() => import("@/pages/min-stock/MinStockPage"));
const MinStockByWarehousePage = lazy(
  () => import("@/pages/min-stock/[warehouseId]/MinStockByWarehousePage")
);
const PaymentMethodsPage = lazy(
  () => import("@/pages/payment-methods/PaymentMethodsPage")
);
const AddPaymentMethodsPage = lazy(
  () => import("@/pages/payment-methods/add/AddPaymentMethodsPage")
);
const EditPaymentMethodPage = lazy(
  () => import("@/pages/payment-methods/edit/EditPaymentMethodPage")
);
const RetailPlansPage = lazy(
  () => import("@/pages/plans-retail/RetailPlansPage")
);
const AddRetailPlanPage = lazy(
  () => import("@/pages/plans-retail/add/AddRetailPlanPage")
);
const EditRetailPlanPage = lazy(
  () => import("@/pages/plans-retail/edit/EditRetailPlanPage")
);
const WholesalerPlansPage = lazy(
  () => import("@/pages/plans-wholesaler/WholesalerPlansPage")
);
const AddWholesalerPlanPage = lazy(
  () => import("@/pages/plans-wholesaler/add/AddWholesalerPlanPage")
);
const EditWholesalerPlanPage = lazy(
  () => import("@/pages/plans-wholesaler/edit/EditWholesalerPlanPage")
);
const PriceListPage = lazy(() => import("@/pages/price-list/PriceListPage"));
const PricingPage = lazy(() => import("@/pages/pricing/PricingPage"));
const PriceUpdatePage = lazy(
  () => import("@/pages/pricing/price-update/PriceUpdatePage")
);
const PricingBulkPage = lazy(
  () => import("@/pages/pricing/bulk/PricingBulkPage")
);
const ModifySelectPage = lazy(
  () => import("@/pages/pricing/bulk/modify-select/ModifySelectPage")
);
const ModifyPricesPreviewPage = lazy(
  () =>
    import("@/pages/pricing/bulk/modify-select/preview/ModifyPricesPreviewPage")
);
const ProductsPage = lazy(() => import("@/pages/products/ProductsPage"));
const AddProductPage = lazy(
  () => import("@/pages/products/add/AddProductPage")
);
const ProductsBulkEdit = lazy(
  () => import("@/pages/products/bulk-edit/ProductsBulkEdit")
);
const EditProductPage = lazy(
  () => import("@/pages/products/edit/EditProductPage")
);
const BulkUploadPage = lazy(
  () => import("@/pages/inventory/bulk-upload/BulkUploadPage")
);
const PurchasesPage = lazy(() => import("@/pages/purchases/PurchasesPage"));
const AddPurchasePage = lazy(
  () => import("@/pages/purchases/add/AddPurchasePage")
);
const PurchaseIngestionPage = lazy(
  () => import("@/pages/purchases/ingestion/PurchaseIngestionPage")
);
const PurchaseRequestsPage = lazy(
  () => import("@/pages/purchases/purchase-requests/PurchaseRequestPage")
);
const AddPurchaseRequestPage = lazy(
  () => import("@/pages/purchases/purchase-requests/add/AddPurchaseRequestPage")
);
const EditPurchaseRequestPage = lazy(
  () =>
    import("@/pages/purchases/purchase-requests/edit/EditPurchaseRequestPage")
);
const SuppliersPage = lazy(() => import("@/pages/suppliers/SuppliersPage"));
const BrandsPage = lazy(() => import("@/pages/suppliers/brands/BrandsPage"));
const TemplatesPage = lazy(() => import("@/pages/templates/TemplatesPage"));
const AddTemplatePage = lazy(
  () => import("@/pages/templates/add/AddTemplatePage")
);
const EditTemplatePage = lazy(
  () => import("@/pages/templates/edit/EditTemplatePage")
);
const TransferPage = lazy(() => import("@/pages/transfer/TransferPage"));
const AddTransferPage = lazy(
  () => import("@/pages/transfer/add/AddTransferPage")
);
const UsersPage = lazy(() => import("@/pages/users/UsersPage"));
const AddUserPage = lazy(() => import("@/pages/users/add/AddUserPage"));
const EditUserPage = lazy(() => import("@/pages/users/edit/EditUserPage"));
const VencimientosPage = lazy(
  () => import("@/pages/vencimientos/Vencimientos")
);
const WarehousesPage = lazy(() => import("@/pages/warehouses/WarehousesPage"));
const AddWarehousePage = lazy(
  () => import("@/pages/warehouses/add/AddWarehousePage")
);
const EditWarehousePage = lazy(
  () => import("@/pages/warehouses/edit/EditWarehousePage")
);
const HomePage = lazy(() => import("../pages/home"));
const MonitorPage = lazy(() => import("@/pages/monitor/MonitorPage"));
const OrdersPage = lazy(() => import("@/pages/orders/OrdersPage"));
const CreateOrderPage = lazy(
  () => import("@/pages/orders/crear/CreateOrderPage")
);
const GiftProductsPage = lazy(
  () => import("@/pages/gift-products/GiftProductsPage")
);
const ReservationsPage = lazy(
  () => import("@/pages/reservations/ReservationsPage")
);
const AddReservationsPage = lazy(
  () => import("@/pages/reservations/add/AddReservationsPage")
);
const ReservableItemsPage = lazy(
  () => import("@/pages/reservations/reservable-items/ReservableItemsPage")
);
const AddReservableItem = lazy(
  () => import("@/pages/reservations/reservable-items/add/AddReservableItem")
);
const EditReservableItemPage = lazy(
  () =>
    import("@/pages/reservations/reservable-items/edit/EditReservableItemPage")
);
const ServicesPage = lazy(
  () => import("@/pages/reservations/services/ServicesPage")
);
const AddServicePage = lazy(
  () => import("@/pages/reservations/services/add/AddServicePage")
);
const EditServicePage = lazy(
  () => import("@/pages/reservations/services/edit/EditServicePage")
);
const BookingConfigPage = lazy(
  () => import("@/pages/reservations/booking-config/BookingConfigPage")
);
const AddBookingPage = lazy(
  () => import("@/pages/reservations/booking-config/add/AddBookingPage")
);
const AvailabilitySlotsPage = lazy(
  () => import("@/pages/reservations/availability-slots/AvailabilitySlotsPage")
);
const AvailabilityExceptionsPage = lazy(
  () =>
    import(
      "@/pages/reservations/availability-exceptions/AvailabilityExceptionsPage"
    )
);
const CancellationPoliciesPage = lazy(
  () =>
    import(
      "@/pages/reservations/cancellation-policies/CancellationPoliciesPage"
    )
);
const AddCancellationPolicyPage = lazy(
  () =>
    import(
      "@/pages/reservations/cancellation-policies/add/AddCancellationPolicyPage"
    )
);
const DepositsPage = lazy(() => import("@/pages/deposits/DepositsPage"));
const AssignDepositPage = lazy(
  () => import("@/pages/deposits/assign/AssignDepositPage")
);
const PassThroughPage = lazy(
  () => import("@/pages/deposits/pass-through/PassThroughPage")
);
const AddDepositPage = lazy(
  () => import("@/pages/deposits/add/AddDepositPage")
);
const EditSupplierPage = lazy(
  () => import("@/pages/suppliers/EditSupplierPage")
);
const AddSupplierPage = lazy(() => import("@/pages/suppliers/AddSupplierPage"));
const AddBrandPage = lazy(
  () => import("@/pages/suppliers/brands/AddBrandPage")
);
const EditBrandPage = lazy(
  () => import("@/pages/suppliers/brands/EditBrandPage")
);
const CashdeskPage = lazy(
  () => import("@/pages/treasury/cashdesk/CashdeskPage")
);
const CashdeskByWarehousePage = lazy(
  () =>
    import("@/pages/treasury/cashdesk/[warehouseId]/CashdeskByWarehousePage")
);
const CashdeskByCashierPage = lazy(
  () => import("@/pages/treasury/cashdesk/by-cashier/CashdeskByCashierPage")
);
const ClosedCashdesksPage = lazy(
  () => import("@/pages/treasury/closed-cashdesks/ClosedCashdesksPage")
);
const ExpensesPage = lazy(
  () => import("@/pages/treasury/expenses/ExpensesPage")
);
const AddExpensePage = lazy(
  () => import("@/pages/treasury/expenses/add/AddExpensePage")
);
const EditExpensePage = lazy(
  () => import("@/pages/treasury/expenses/edit/EditExpensePage")
);
const CommissionsPage = lazy(
  () => import("@/pages/treasury/comissions/CommissionsPage")
);
const ChecksPage = lazy(() => import("@/pages/treasury/checks/ChecksPage"));
const ChecksPortfolioPage = lazy(
  () => import("@/pages/treasury/checks-portfolio/ChecksPortfolioPage")
);
const ConfigurationPage = lazy(
  () => import("@/pages/configuration/ConfigurationPage")
);
const MercadoPagoPage = lazy(
  () =>
    import("@/pages/configuration/integrations/mercado-pago/MercadoPagoPage")
);
const MercadoLibrePage = lazy(
  () =>
    import("@/pages/configuration/integrations/mercado-libre/MercadoLibrePage")
);
const InvoicesPage = lazy(() => import("@/pages/orders/invoices/InvoicesPage"));
const CouponsPage = lazy(() => import("@/pages/coupons/coupons/CouponsPage"));
const AddCouponsPage = lazy(
  () => import("@/pages/coupons/coupons/add/AddCouponPage")
);
const OffersPage = lazy(() => import("@/pages/coupons/offers/OffersPage"));
const AddOfferPage = lazy(
  () => import("@/pages/coupons/offers/add/AddOfferPage")
);
const EditCouponPage = lazy(
  () => import("@/pages/coupons/coupons/edit/EditCouponPage")
);
const EditOfferPage = lazy(
  () => import("@/pages/coupons/offers/edit/EditOfferPage")
);
const NotificationsPage = lazy(
  () => import("@/pages/notifications/NotificationsPage")
);
const BulkPage = lazy(
  () => import("@/pages/inventory/[warehouseId]/[bulkId]/BulkPage")
);
const CreateQuickOrderPage = lazy(
  () => import("@/pages/orders/crear/quick/CreateQuickOrderPage")
);
const PendientesDeLaCaja = lazy(
  () => import("@/pages/orders/caja/PendientesDeLaCaja")
);
const CajasDeEscritorioPage = lazy(
  () => import("@/pages/orders/cajas-de-escritorio/CajasDeEscritorioPage")
);
const QuickSelectWarehousePage = lazy(
  () => import("@/pages/orders/crear/quick/SelectWarehousePage")
);
// ChatPage es named export — lo envolvemos para que React.lazy reciba un default.
const ChatPage = lazy(() =>
  import("@/pages/chat/ChatPage").then((m) => ({ default: m.ChatPage }))
);
const ReturnPage = lazy(() => import("@/pages/orders/return/ReturnPage"));
const PendingPickupsPage = lazy(
  () => import("@/pages/orders/pending-pickups/PendingPickupsPage")
);
const PendingShipmentPage = lazy(
  () => import("@/pages/orders/pending-shipment/PendingShipmentPage")
);
const ShipmentsPage = lazy(
  () => import("@/pages/orders/shipments/ShipmentsPage")
);
const SubscriptionPage = lazy(
  () => import("@/pages/subscription/SubscriptionPage")
);
const BigSupplierCatalogPage = lazy(
  () => import("@/pages/big-supplier/catalog/BigSupplierCatalogPage")
);
const PendingPriceUpdatePage = lazy(
  () =>
    import("@/pages/big-supplier/pending-price-update/PendingPriceUpdatePage")
);
const BigSupplierGuard = lazy(
  () => import("@/pages/big-supplier-seller/BigSupplierGuard")
);
const PublishedProductsPage = lazy(
  () => import("@/pages/big-supplier-seller/products/PublishedProductsPage")
);
const MyClientsPage = lazy(
  () => import("@/pages/big-supplier-seller/clients/MyClientsPage")
);
const MyRequestsPage = lazy(
  () => import("@/pages/big-supplier-seller/requests/MyRequestsPage")
);
const MyOrdersPage = lazy(
  () => import("@/pages/big-supplier-seller/orders/MyOrdersPage")
);
const MyB2BPaymentsPage = lazy(
  () => import("@/pages/purchases/b2b-payments/MyB2BPaymentsPage")
);
const MyPaymentRequestsPage = lazy(
  () =>
    import("@/pages/big-supplier-seller/payment-requests/MyPaymentRequestsPage")
);
const MyPriceUpdatesPage = lazy(
  () => import("@/pages/big-supplier-seller/price-updates/MyPriceUpdatesPage")
);
const PatientsListPage = lazy(
  () => import("@/pages/clinical/patients/PatientsListPage")
);
const AddPatientPage = lazy(
  () => import("@/pages/clinical/patients/add/AddPatientPage")
);
const ImportPatientsPage = lazy(
  () => import("@/pages/clinical/patients/import/ImportPatientsPage")
);
const PatientDetailPage = lazy(
  () => import("@/pages/clinical/patients/detail/PatientDetailPage")
);
const InsurancePlansListPage = lazy(
  () => import("@/pages/clinical/insurance-plans/InsurancePlansListPage")
);
const AddInsurancePlanPage = lazy(
  () => import("@/pages/clinical/insurance-plans/add/AddInsurancePlanPage")
);
const EditInsurancePlanPage = lazy(
  () => import("@/pages/clinical/insurance-plans/edit/EditInsurancePlanPage")
);
const VeterinariansListPage = lazy(
  () => import("@/pages/clinical/veterinarians/VeterinariansListPage")
);
const AddVeterinarianPage = lazy(
  () => import("@/pages/clinical/veterinarians/add/AddVeterinarianPage")
);
const EditVeterinarianPage = lazy(
  () => import("@/pages/clinical/veterinarians/edit/EditVeterinarianPage")
);
const VeterinaryServicesListPage = lazy(
  () =>
    import("@/pages/clinical/veterinary-services/VeterinaryServicesListPage")
);
const VeterinaryServicesPickerPage = lazy(
  () =>
    import("@/pages/clinical/veterinary-services/VeterinaryServicesPickerPage")
);
const AddVeterinaryServicePage = lazy(
  () =>
    import("@/pages/clinical/veterinary-services/add/AddVeterinaryServicePage")
);
const VisitsListPage = lazy(
  () => import("@/pages/clinical/visits/VisitsListPage")
);
const VisitDetailPage = lazy(
  () => import("@/pages/clinical/visits/VisitDetailPage")
);
const AddVisitPage = lazy(
  () => import("@/pages/clinical/visits/add/AddVisitPage")
);
const OccupancyResourcesListPage = lazy(
  () =>
    import("@/pages/clinical/occupancy-resources/OccupancyResourcesListPage")
);
const AddOccupancyResourcePage = lazy(
  () =>
    import("@/pages/clinical/occupancy-resources/add/AddOccupancyResourcePage")
);
const EditOccupancyResourcePage = lazy(
  () =>
    import(
      "@/pages/clinical/occupancy-resources/edit/EditOccupancyResourcePage"
    )
);
const StaysListPage = lazy(
  () => import("@/pages/clinical/stays/StaysListPage")
);
const StayDetailPage = lazy(
  () => import("@/pages/clinical/stays/StayDetailPage")
);
const AddStayPage = lazy(
  () => import("@/pages/clinical/stays/add/AddStayPage")
);
const RemindersListPage = lazy(
  () => import("@/pages/clinical/reminders/RemindersListPage")
);
const ProtocolsListPage = lazy(
  () => import("@/pages/clinical/protocols/ProtocolsListPage")
);
const AddVaccineProtocolPage = lazy(
  () => import("@/pages/clinical/protocols/add/AddVaccineProtocolPage")
);
const AddDewormerProtocolPage = lazy(
  () => import("@/pages/clinical/protocols/add/AddDewormerProtocolPage")
);
const EditVaccineProtocolPage = lazy(
  () => import("@/pages/clinical/protocols/edit/EditVaccineProtocolPage")
);
const EditDewormerProtocolPage = lazy(
  () => import("@/pages/clinical/protocols/edit/EditDewormerProtocolPage")
);
const CertificateTemplatesListPage = lazy(
  () =>
    import(
      "@/pages/clinical/certificate-templates/CertificateTemplatesListPage"
    )
);
const AddCertificateTemplatePage = lazy(
  () =>
    import(
      "@/pages/clinical/certificate-templates/add/AddCertificateTemplatePage"
    )
);
const EditCertificateTemplatePage = lazy(
  () =>
    import(
      "@/pages/clinical/certificate-templates/edit/EditCertificateTemplatePage"
    )
);
const CertificatesListPage = lazy(
  () => import("@/pages/clinical/certificates/CertificatesListPage")
);
const ClinicalDashboardPage = lazy(
  () => import("@/pages/clinical/ClinicalDashboardPage")
);
const CalendarPage = lazy(
  () => import("@/pages/clinical/calendar/CalendarPage")
);
const WaitingRoomPage = lazy(
  () => import("@/pages/clinical/waiting-room/WaitingRoomPage")
);
const QueueConfigPage = lazy(
  () => import("@/pages/clinical/config/QueueConfigPage")
);
const AddQueueCategoryPage = lazy(
  () =>
    import("@/pages/clinical/config/queue-categories/add/AddQueueCategoryPage")
);
const EditQueueCategoryPage = lazy(
  () =>
    import(
      "@/pages/clinical/config/queue-categories/edit/EditQueueCategoryPage"
    )
);
const AddConsultorioPage = lazy(
  () => import("@/pages/clinical/config/consultorios/add/AddConsultorioPage")
);
const EditConsultorioPage = lazy(
  () => import("@/pages/clinical/config/consultorios/edit/EditConsultorioPage")
);

/**
 * Mountpoint para el listener de workflows pendientes. Debe estar dentro
 * del QueryClientProvider porque el hook usa useQueryClient.
 */
function PendingWorkflowsListenerMount() {
  usePendingWorkflowsListener();
  // Listener específico de catalog: matchea `catalog.attribute_workflow.completed`
  // (publicado desde trd-core al terminar productAttributeRebuildWorkflow o
  // productAttributeUpdateWorkflow) y libera el guard por producto.
  usePendingProductWorkflowsSocketListener();
  return null;
}

/**
 * Fallback que se muestra mientras React descarga el chunk de la página
 * destino. Se ve solo la primera vez que el usuario navega a una ruta nueva
 * (después queda en cache del browser).
 */
function PageLoadingFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function AppRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <PendingWorkflowsListenerMount />
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginOne />} />
          {/* Handoff de impersonación desde la consola (#92). Pública y sin
              layout: el token viene en el FRAGMENTO y se canjea por sesión. */}
          <Route path="/act-as" element={<ActAsPage />} />

          {/* Invitacion identity: el empleado setea su password (publico, sin
              login — el `code` del link es la credencial). */}
          <Route path="/set-password" element={<SetPasswordPage />} />

          {/*
            OAuth callback bridge. Sits outside ClientLayout because it must
            render in the popup window (no sidebar / auth gating) and in the
            desktop OS browser tab that then bounces to `trud://`.
          */}
          <Route
            path="/integrations/:provider/:status"
            element={<OAuthCallbackPage />}
          />

          <Route
            path="/"
            element={
              <ClientLayout pathname="/">
                <HomePage />
              </ClientLayout>
            }
          />

          {/* Chat AI */}
          <Route
            path="/chat"
            element={
              <ClientLayout resource={RESOURCES.AI_CHAT} action="view">
                <ChatPage />
              </ClientLayout>
            }
          />

          {/* Usuarios */}
          <Route path="usuarios">
            <Route
              index
              element={
                <ClientLayout resource={RESOURCES.AUTH_USERS} action="view">
                  <UsersPage />
                </ClientLayout>
              }
            />

            <Route
              path="agregar"
              element={
                <ClientLayout resource={RESOURCES.AUTH_USERS} action="modify">
                  <AddUserPage />
                </ClientLayout>
              }
            />

            <Route
              path="editar/:id"
              element={
                <ClientLayout resource={RESOURCES.AUTH_USERS} action="modify">
                  <EditUserPage />
                </ClientLayout>
              }
            />
          </Route>

          {/* Clientes */}
          <Route path="clientes">
            {/* Clientes index */}
            <Route
              index
              element={
                <ClientLayout resource={RESOURCES.PEOPLE_BUYERS} action="view">
                  <BuyersPage />
                </ClientLayout>
              }
            />

            {/* Agregar cliente */}
            <Route
              path="agregar"
              element={
                <ClientLayout
                  resource={RESOURCES.PEOPLE_BUYERS}
                  action="modify"
                >
                  <AddBuyerPage />
                </ClientLayout>
              }
            />

            {/* Editar cliente */}
            <Route
              path="editar/:id"
              element={
                <ClientLayout
                  resource={RESOURCES.PEOPLE_BUYERS}
                  action="modify"
                >
                  <EditBuyerPage />
                </ClientLayout>
              }
            />

            {/* Deuda inicial */}
            <Route path="deuda-inicial">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.COLLECTION_INITIAL_DEBT}
                    action="view"
                  >
                    <InitialDebtPage />
                  </ClientLayout>
                }
              />

              {/* Agregar deuda inicial */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.COLLECTION_INITIAL_DEBT}
                    action="modify"
                  >
                    <AddInitialDebtPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Deudores */}
            <Route
              path="deudores"
              element={
                <ClientLayout resource={RESOURCES.PEOPLE_BUYERS} action="view">
                  <DebtorsPage />
                </ClientLayout>
              }
            />
          </Route>

          {/* Veterinaria — F1: Patients. Sub-rutas (vets, visits, stays,
              vaccinations, reminders, certificates, prescriptions, blood-bank)
              se agregan en sus fases respectivas (F2-F9). */}
          <Route path="clinical">
            <Route
              index
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <ClinicalDashboardPage />
                </ClientLayout>
              }
            />
            <Route
              path="patients"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <PatientsListPage />
                </ClientLayout>
              }
            />
            <Route
              path="patients/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddPatientPage />
                </ClientLayout>
              }
            />
            <Route
              path="patients/import"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <ImportPatientsPage />
                </ClientLayout>
              }
            />
            <Route
              path="patients/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <PatientDetailPage />
                </ClientLayout>
              }
            />
            <Route
              path="insurance-plans"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <InsurancePlansListPage />
                </ClientLayout>
              }
            />
            <Route
              path="insurance-plans/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddInsurancePlanPage />
                </ClientLayout>
              }
            />
            <Route
              path="insurance-plans/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditInsurancePlanPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinarians"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <VeterinariansListPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinarians/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddVeterinarianPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinarians/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditVeterinarianPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinary-services"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <VeterinaryServicesPickerPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinary-services/:warehouseId"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <VeterinaryServicesListPage />
                </ClientLayout>
              }
            />
            <Route
              path="veterinary-services/:warehouseId/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddVeterinaryServicePage />
                </ClientLayout>
              }
            />
            <Route
              path="visits"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <VisitsListPage />
                </ClientLayout>
              }
            />
            <Route
              path="visits/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddVisitPage />
                </ClientLayout>
              }
            />
            <Route
              path="visits/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <VisitDetailPage />
                </ClientLayout>
              }
            />
            <Route
              path="occupancy-resources"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <OccupancyResourcesListPage />
                </ClientLayout>
              }
            />
            <Route
              path="occupancy-resources/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddOccupancyResourcePage />
                </ClientLayout>
              }
            />
            <Route
              path="occupancy-resources/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditOccupancyResourcePage />
                </ClientLayout>
              }
            />
            <Route
              path="stays"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <StaysListPage />
                </ClientLayout>
              }
            />
            <Route
              path="stays/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddStayPage />
                </ClientLayout>
              }
            />
            <Route
              path="stays/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <StayDetailPage />
                </ClientLayout>
              }
            />
            <Route
              path="reminders"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <RemindersListPage />
                </ClientLayout>
              }
            />
            <Route
              path="protocols"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <ProtocolsListPage />
                </ClientLayout>
              }
            />
            <Route
              path="protocols/vaccines/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddVaccineProtocolPage />
                </ClientLayout>
              }
            />
            <Route
              path="protocols/vaccines/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditVaccineProtocolPage />
                </ClientLayout>
              }
            />
            <Route
              path="protocols/dewormers/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddDewormerProtocolPage />
                </ClientLayout>
              }
            />
            <Route
              path="protocols/dewormers/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditDewormerProtocolPage />
                </ClientLayout>
              }
            />
            <Route
              path="calendar"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <CalendarPage />
                </ClientLayout>
              }
            />
            <Route
              path="waiting-room"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <WaitingRoomPage />
                </ClientLayout>
              }
            />
            <Route
              path="config/turnero"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <QueueConfigPage />
                </ClientLayout>
              }
            />
            <Route
              path="config/queue-categories/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddQueueCategoryPage />
                </ClientLayout>
              }
            />
            <Route
              path="config/queue-categories/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditQueueCategoryPage />
                </ClientLayout>
              }
            />
            <Route
              path="config/consultorios/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddConsultorioPage />
                </ClientLayout>
              }
            />
            <Route
              path="config/consultorios/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditConsultorioPage />
                </ClientLayout>
              }
            />
            <Route
              path="certificate-templates"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <CertificateTemplatesListPage />
                </ClientLayout>
              }
            />
            <Route
              path="certificate-templates/add"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <AddCertificateTemplatePage />
                </ClientLayout>
              }
            />
            <Route
              path="certificate-templates/edit/:id"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="modify">
                  <EditCertificateTemplatePage />
                </ClientLayout>
              }
            />
            <Route
              path="certificates"
              element={
                <ClientLayout resource={CLINICAL_RESOURCES} action="view">
                  <CertificatesListPage />
                </ClientLayout>
              }
            />
          </Route>

          {/* Proveedores */}
          <Route path="proveedores">
            {/* Proveedores index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.SUPPLIER_SUPPLIERS}
                  action="view"
                >
                  <SuppliersPage />
                </ClientLayout>
              }
            />

            {/* Agregar proveedor */}
            <Route
              path="agregar"
              element={
                <ClientLayout
                  resource={RESOURCES.SUPPLIER_SUPPLIERS}
                  action="modify"
                >
                  <AddSupplierPage />
                </ClientLayout>
              }
            />

            {/* Editar proveedor */}
            <Route
              path="editar/:id"
              element={
                <ClientLayout
                  resource={RESOURCES.SUPPLIER_SUPPLIERS}
                  action="modify"
                >
                  <EditSupplierPage />
                </ClientLayout>
              }
            />
            {/* Marcas */}
            <Route path="marcas">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.SUPPLIER_BRANDS}
                    action="view"
                  >
                    <BrandsPage />
                  </ClientLayout>
                }
              />

              {/* Agregar marca */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.SUPPLIER_BRANDS}
                    action="modify"
                  >
                    <AddBrandPage />
                  </ClientLayout>
                }
              />
              {/* Editar marca */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.SUPPLIER_BRANDS}
                    action="modify"
                  >
                    <EditBrandPage />
                  </ClientLayout>
                }
              />
            </Route>
          </Route>

          {/* Inventario */}
          <Route path="inventario">
            {/* Inventario index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.INVENTORY_INVENTORIES}
                  action="view"
                >
                  <InventoryPage />
                </ClientLayout>
              }
            />

            {/* Inventario por local */}
            <Route path="local/:warehouseId">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <WarehouseInventoryPage />
                  </ClientLayout>
                }
              />

              <Route
                path=":bulkId"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <BulkPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Categorías */}
            <Route path="categorias">
              {/* Categorías index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_CATEGORIES}
                    action="view"
                  >
                    <CategoriesPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar categoría */}
              <Route path="agregar">
                <Route
                  index
                  element={
                    <ClientLayout
                      resource={RESOURCES.CATALOG_CATEGORIES}
                      action="modify"
                    >
                      <AddCategoryPage />
                    </ClientLayout>
                  }
                ></Route>

                {/* Agregar subcategoría */}
                <Route
                  path="subcategoria/:categoryId"
                  element={
                    <ClientLayout
                      resource={RESOURCES.CATALOG_CATEGORIES}
                      action="modify"
                    >
                      <AddSubcategoryPage />
                    </ClientLayout>
                  }
                ></Route>
              </Route>

              {/* Editar categoría */}
              <Route
                path="editar/:categoryId"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_CATEGORIES}
                    action="modify"
                  >
                    <EditCategoryPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Grupos de productos */}
            {/*
              Gateado con `catalog/products` a proposito: un grupo es un
              conjunto de variantes, asi que quien administra productos
              administra sus grupos. Un recurso propio (`catalog/product-groups`)
              es la evolucion natural, pero necesita el enum IdPath de core, el
              artefacto idpath.surface.json, el registry de octopus e identity —
              gatearlo hoy contra un recurso que el entitlement no sirve dejaria
              la pantalla invisible para todos.
            */}
            <Route path="grupos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="view"
                  >
                    <ProductGroupsPage />
                  </ClientLayout>
                }
              />

              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <AddProductGroupPage />
                  </ClientLayout>
                }
              />

              <Route
                path="editar/:groupId"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <EditProductGroupPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Grupos de consumo */}
            {/*
              Gateado con `catalog/products`, por lo mismo que los grupos de
              productos: quien administra productos administra cuanto duran. Un
              recurso propio necesita el enum IdPath de core, el artefacto
              idpath.surface.json y el registry de octopus e identity; gatearlo
              hoy contra un recurso que el entitlement no sirve dejaria la
              pantalla invisible para todos.
            */}
            <Route path="grupos-de-consumo">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="view"
                  >
                    <ConsumptionGroupsPage />
                  </ClientLayout>
                }
              />

              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <AddConsumptionGroupPage />
                  </ClientLayout>
                }
              />

              <Route
                path="editar/:groupId"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <EditConsumptionGroupPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Locales */}
            <Route path="locales">
              {/* Locales index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_WAREHOUSES}
                    action="view"
                  >
                    <WarehousesPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar Local */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_WAREHOUSES}
                    action="modify"
                  >
                    <AddWarehousePage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar Local */}
              <Route
                path="editar/:warehouseId"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_WAREHOUSES}
                    action="modify"
                  >
                    <EditWarehousePage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Productos */}
            <Route path="productos">
              {/* Productos index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="view"
                  >
                    <ProductsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar Productos */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <AddProductPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar Producto */}
              <Route
                path="editar/:productId"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <EditProductPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar Producto */}
              <Route
                path="editar-masivo"
                element={
                  <ClientLayout
                    resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <ProductsBulkEdit />
                  </ClientLayout>
                }
              ></Route>

              {/* Carga masiva (wizard de import via Excel) */}
              <Route path="carga-masiva">
                <Route
                  index
                  element={
                    <ClientLayout
                      resource={RESOURCES.CATALOG_PRODUCTS}
                      action="modify"
                    >
                      <BulkUploadPage />
                    </ClientLayout>
                  }
                />
                <Route
                  path=":bulkId"
                  element={
                    <ClientLayout
                      resource={RESOURCES.CATALOG_PRODUCTS}
                      action="modify"
                    >
                      <BulkUploadPage />
                    </ClientLayout>
                  }
                />
              </Route>
            </Route>

            {/* Sueltos — gate por su propio recurso (inventory/loose-products),
                no catalog/products: antes un empleado sin sueltos podía entrar
                por URL directa aunque no lo tuviera (#49). */}
            <Route path="sueltos">
              {/* Sueltos index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_LOOSE_PRODUCTS}
                    action="view"
                  >
                    <LoosePage />
                  </ClientLayout>
                }
              ></Route>

              {/* Sueltos por local */}
              <Route path=":warehouseId">
                <Route
                  index
                  element={
                    <ClientLayout
                      resource={RESOURCES.INVENTORY_LOOSE_PRODUCTS}
                      action="view"
                    >
                      <LooseByWarehousePage />
                    </ClientLayout>
                  }
                ></Route>

                {/* Agregar Suelto */}
                <Route
                  path="agregar"
                  element={
                    <ClientLayout
                      resource={RESOURCES.INVENTORY_LOOSE_PRODUCTS}
                      action="modify"
                    >
                      <AddLoosePage />
                    </ClientLayout>
                  }
                ></Route>
              </Route>

              {/* Agregar Suelto */}
              {/* <Route
                path="agregar"
                element={
                  <ClientLayout resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <AddProductPage />
                  </ClientLayout>
                }
              ></Route> */}

              {/* Editar Suelto */}
              {/* <Route
                path="editar/:productId"
                element={
                  <ClientLayout resource={RESOURCES.CATALOG_PRODUCTS}
                    action="modify"
                  >
                    <EditProductPage />
                  </ClientLayout>
                }
              ></Route> */}
            </Route>

            {/* Transferencia de mercadería */}
            <Route path="transferencia-de-mercaderia">
              {/* Transferencia de mercadería index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_TRANSFER}
                    action="view"
                  >
                    <TransferPage />
                  </ClientLayout>
                }
              ></Route>

              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_TRANSFER}
                    action="modify"
                  >
                    <AddTransferPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Stocks minimos */}
            <Route path="stocks-minimos">
              {/* Stocks minimos index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <MinStockPage />
                  </ClientLayout>
                }
              />

              {/* Stocks minimos por local */}
              <Route
                path="local/:warehouseId"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <MinStockByWarehousePage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Valuación de inventario */}
            <Route path="valuacion">
              {/* Valuación de inventario index */}
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <InventoryValuationPage />
                  </ClientLayout>
                }
              />

              {/* Detalle de valuacion por local */}
              <Route
                path=":warehouseId"
                element={
                  <ClientLayout
                    resource={RESOURCES.INVENTORY_INVENTORIES}
                    action="view"
                  >
                    <InventoryValuationDetailsPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
          </Route>

          {/* Precios */}
          <Route path="precios">
            {/* Precios index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.PRICING_MODIFY_PRICE}
                  action="modify"
                >
                  <PricingPage />
                </ClientLayout>
              }
            />
            {/* Actualizar precios (wizard durable via Excel del proveedor) */}
            <Route path="actualizar-precios">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <PriceUpdatePage />
                  </ClientLayout>
                }
              />
              <Route
                path=":jobId"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <PriceUpdatePage />
                  </ClientLayout>
                }
              />
            </Route>
            {/* Plantillas / Templates */}
            <Route path="plantillas">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <TemplatesPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar plantilla */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <AddTemplatePage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar plantilla */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <EditTemplatePage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
            {/* Aumento masivo */}
            <Route path="aumento-masivo">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <PricingBulkPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Modificar seleccionados */}
              <Route path="modificar-seleccionados">
                <Route
                  index
                  element={
                    <ClientLayout
                      resource={RESOURCES.PRICING_MODIFY_PRICE}
                      action="modify"
                    >
                      <ModifySelectPage />
                    </ClientLayout>
                  }
                ></Route>

                {/* Vista previa */}
                <Route
                  path="vista-previa"
                  element={
                    <ClientLayout
                      resource={RESOURCES.PRICING_MODIFY_PRICE}
                      action="modify"
                    >
                      <ModifyPricesPreviewPage />
                    </ClientLayout>
                  }
                ></Route>
              </Route>
            </Route>
            {/* Listas Minoristas */}
            <Route path="listas-minoristas">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="view"
                  >
                    <RetailListsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Crear lista minorista */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <AddRetailListPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar lista minorista */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="modify"
                  >
                    <EditRetailListPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
            {/* Planes Minoristas */}
            <Route path="planes-minoristas">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_PLAN}
                    action="view"
                  >
                    <RetailPlansPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Crear plan minorista */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_PLAN}
                    action="modify"
                  >
                    <AddRetailPlanPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar plan minorista */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_PLAN}
                    action="modify"
                  >
                    <EditRetailPlanPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
            {/* Planes Mayoristas */}
            <Route path="planes-mayoristas">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_WHOLESALER_PLAN}
                    action="view"
                  >
                    <WholesalerPlansPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Crear plan mayorista */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_WHOLESALER_PLAN}
                    action="modify"
                  >
                    <AddWholesalerPlanPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar plan mayorista */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_PRICE_WHOLESALER_PLAN}
                    action="modify"
                  >
                    <EditWholesalerPlanPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Medios de pago y recargos */}
            <Route path="recargos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_CHECKOUT_FEES}
                    action="view"
                  >
                    <PaymentMethodsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Crear recargo */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_CHECKOUT_FEES}
                    action="modify"
                  >
                    <AddPaymentMethodsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar recargo */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_CHECKOUT_FEES}
                    action="modify"
                  >
                    <EditPaymentMethodPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Listas de precios */}
            <Route path="listas-de-precios">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PRICING_MODIFY_PRICE}
                    action="view"
                  >
                    <PriceListPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
          </Route>

          {/* Compras */}
          <Route path="compras">
            <Route
              path="pagos-b2b"
              element={
                <ClientLayout
                  resource={RESOURCES.DISBURSEMENT_DISBURSEMENTS}
                  action="view"
                >
                  <MyB2BPaymentsPage />
                </ClientLayout>
              }
            ></Route>
            {/* Compras index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.PURCHASE_PURCHASES}
                  action="view"
                >
                  <PurchasesPage />
                </ClientLayout>
              }
            />

            {/* Crear compra */}
            <Route
              path="crear"
              element={
                <ClientLayout
                  resource={RESOURCES.PURCHASE_PURCHASES}
                  action="modify"
                >
                  <AddPurchasePage />
                </ClientLayout>
              }
            ></Route>

            {/* Cargar factura (Purchase Ingestion — OCR agentic vía trd-agent) */}
            <Route
              path="cargar-factura"
              element={
                <ClientLayout
                  resource={RESOURCES.PURCHASE_PURCHASES}
                  action="modify"
                >
                  <PurchaseIngestionPage />
                </ClientLayout>
              }
            ></Route>

            {/* Solicitud de compra */}
            <Route path="solicitud-compra">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PURCHASE_STOCK_ORDER}
                    action="view"
                  >
                    <PurchaseRequestsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar solicitud*/}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.PURCHASE_STOCK_ORDER}
                    action="modify"
                  >
                    <AddPurchaseRequestPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar solicitud */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.PURCHASE_STOCK_ORDER}
                    action="modify"
                  >
                    <EditPurchaseRequestPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Vencimientos — compras Y ventas. El gate de la ruta acepta
                cualquiera de los dos recursos; la pantalla despues muestra
                solo la cara que el usuario puede ver (ADR-0003). */}
            <Route path="vencimientos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={[
                      RESOURCES.PURCHASE_PURCHASES,
                      RESOURCES.ORDER_ORDERS,
                    ]}
                    action="view"
                  >
                    <VencimientosPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Pagos */}
            <Route path="pagos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.DISBURSEMENT_DISBURSEMENTS}
                    action="view"
                  >
                    <DisbursementsPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar pago */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.DISBURSEMENT_DISBURSEMENTS}
                    action="modify"
                  >
                    <AddDisbursementPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Asignar. El permiso es el mismo que el del botón «Asignar
                  ahora» y el del mapa del menú: RECURSO_PARA_ASIGNAR. */}
              <Route
                path="asignar/:id"
                element={
                  <ClientLayout
                    resource={RECURSO_PARA_ASIGNAR.pago}
                    action="modify"
                  >
                    <AssignDisbursementPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Deuda inicial */}
            <Route path="deuda-inicial">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.PURCHASE_INITIAL_DEBT}
                    action="view"
                  >
                    <InitialPurchasesPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar deuda inicial */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.PURCHASE_INITIAL_DEBT}
                    action="modify"
                  >
                    <AddInitialPurchasePage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
          </Route>

          {/* Ventas */}
          <Route path="ventas">
            {/* Ventas index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="view"
                  planFeature={[
                    "order/orders",
                    "order/simple",
                    "order/advanced",
                  ]}
                >
                  <OrdersPage />
                </ClientLayout>
              }
            />

            {/* Recordatorios de reposición — la cola de lo que se le está por
                acabar a cada cliente. Gateada con `order/orders` porque el
                recordatorio nace de una VENTA: quien ve las ventas ve lo que
                hay que reponer. */}
            <Route
              path="recordatorios"
              element={
                <ClientLayout resource={RESOURCES.ORDER_ORDERS} action="view">
                  <RepurchaseRemindersPage />
                </ClientLayout>
              }
            />

            {/* Envíos — lo que sale a domicilio y todavía no se entregó.
                De acá sale la hoja de ruta que se lleva el transportista
                (ADR-0020). */}
            <Route
              path="envios"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature={[
                    "order/orders",
                    "order/simple",
                    "order/advanced",
                  ]}
                >
                  <ShipmentsPage />
                </ClientLayout>
              }
            />

            {/* Retiros pendientes — la bandeja del local que ENTREGA lo que
                se vendió y cobró en otra sucursal. */}
            <Route
              path="retiros-pendientes"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature={[
                    "order/orders",
                    "order/simple",
                    "order/advanced",
                  ]}
                >
                  <PendingPickupsPage />
                </ClientLayout>
              }
            />

            {/* Preparación de envíos: qué despachar hoy, sumado por producto */}
            <Route
              path="preparacion-de-envios"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature={[
                    "order/orders",
                    "order/simple",
                    "order/advanced",
                  ]}
                >
                  <PendingShipmentPage />
                </ClientLayout>
              }
            />

            {/* Crear venta */}
            <Route
              path="crear"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature="order/advanced"
                >
                  <CreateOrderPage />
                </ClientLayout>
              }
            />

            {/* Crear venta */}
            <Route path="crear-rapida">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.ORDER_ORDERS}
                    action="modify"
                    noSidebar={true}
                    planFeature="order/simple"
                  >
                    <QuickSelectWarehousePage />
                  </ClientLayout>
                }
              ></Route>

              <Route
                path=":warehouseId"
                element={
                  <ClientLayout
                    resource={RESOURCES.ORDER_ORDERS}
                    action="modify"
                    noSidebar={true}
                    planFeature="order/simple"
                  >
                    <CreateQuickOrderPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* La cola de la caja del escritorio (#338 AC-D) */}
            <Route
              path="caja/pendientes"
              element={
                <ClientLayout resource={RESOURCES.ORDER_ORDERS} action="view">
                  <PendientesDeLaCaja />
                </ClientLayout>
              }
            />

            {/* La pantalla del dueño de las cajas de escritorio (#346 AC-4) */}
            <Route
              path="cajas-de-escritorio"
              element={
                <ClientLayout
                  resource={RESOURCES.INVENTORY_WAREHOUSES}
                  action="view"
                >
                  <CajasDeEscritorioPage />
                </ClientLayout>
              }
            />

            {/* Devolucion */}
            <Route
              path="devolucion/:orderId"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature="order/advanced"
                >
                  <ReturnPage />
                </ClientLayout>
              }
            />

            {/* Facturación */}
            <Route
              path="facturacion"
              element={
                <ClientLayout
                  resource={RESOURCES.ORDER_ORDERS}
                  action="modify"
                  planFeature="order/advanced"
                >
                  <InvoicesPage />
                </ClientLayout>
              }
            />

            {/* Productos regalo */}
            <Route
              path="productos-regalo"
              element={
                <ClientLayout pathname="owner">
                  <GiftProductsPage />
                </ClientLayout>
              }
            />
          </Route>

          {/* Reservas */}
          <Route path="reservas">
            {/* Reservas index */}
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.RESERVATION_RESERVATIONS}
                  action="view"
                >
                  <ReservationsPage />
                </ClientLayout>
              }
            />

            {/* crear reserva */}
            <Route
              path="crear"
              element={
                <ClientLayout
                  resource={RESOURCES.RESERVATION_RESERVATIONS}
                  action="modify"
                >
                  <AddReservationsPage />
                </ClientLayout>
              }
            />

            {/* Recursos */}
            <Route path="recursos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_RESERVABLES}
                    action="view"
                  >
                    <ReservableItemsPage />
                  </ClientLayout>
                }
              />

              {/* Crear recurso  */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_RESERVABLES}
                    action="modify"
                  >
                    <AddReservableItem />
                  </ClientLayout>
                }
              />

              {/* Editar recurso */}
              <Route
                path="editar/:reservableId"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_RESERVABLES}
                    action="modify"
                  >
                    <EditReservableItemPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Servicios */}
            <Route path="servicios">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_SERVICES}
                    action="view"
                  >
                    <ServicesPage />
                  </ClientLayout>
                }
              />

              {/* Crear servicio  */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_SERVICES}
                    action="modify"
                  >
                    <AddServicePage />
                  </ClientLayout>
                }
              />

              {/* Editar servicio */}
              <Route
                path="editar/:serviceId"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_SERVICES}
                    action="modify"
                  >
                    <EditServicePage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Booking online */}
            <Route path="booking">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_BOOKINGS}
                    action="view"
                  >
                    <BookingConfigPage />
                  </ClientLayout>
                }
              />

              {/* Crear booking */}
              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_BOOKINGS}
                    action="modify"
                  >
                    <AddBookingPage />
                  </ClientLayout>
                }
              />
            </Route>

            {/* Horarios de disponibilidad */}
            <Route
              path="recursos/:reservableId/horarios"
              element={
                <ClientLayout
                  resource={RESOURCES.RESERVATION_RESERVABLES}
                  action="modify"
                >
                  <AvailabilitySlotsPage />
                </ClientLayout>
              }
            />

            {/* Excepciones de disponibilidad */}
            <Route
              path="recursos/:reservableId/excepciones"
              element={
                <ClientLayout
                  resource={RESOURCES.RESERVATION_RESERVABLES}
                  action="modify"
                >
                  <AvailabilityExceptionsPage />
                </ClientLayout>
              }
            />

            {/* Políticas de cancelación */}
            <Route path="politicas-cancelacion">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_RESERVATIONS}
                    action="view"
                  >
                    <CancellationPoliciesPage />
                  </ClientLayout>
                }
              />

              <Route
                path="crear"
                element={
                  <ClientLayout
                    resource={RESOURCES.RESERVATION_RESERVATIONS}
                    action="modify"
                  >
                    <AddCancellationPolicyPage />
                  </ClientLayout>
                }
              />
            </Route>
          </Route>

          {/* Cobros */}
          <Route path="cobros">
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.COLLECTION_DEPOSITS}
                  action="view"
                >
                  <DepositsPage />
                </ClientLayout>
              }
            ></Route>

            {/* Agregar cobro */}
            <Route
              path="agregar"
              element={
                <ClientLayout
                  resource={RESOURCES.COLLECTION_DEPOSITS}
                  action="modify"
                >
                  <AddDepositPage />
                </ClientLayout>
              }
            ></Route>

            {/* Asignar. El permiso es el mismo que el del botón «Asignar
                ahora» y el del mapa del menú: RECURSO_PARA_ASIGNAR. */}
            <Route
              path="asignar/:id"
              element={
                <ClientLayout
                  resource={RECURSO_PARA_ASIGNAR.cobro}
                  action="modify"
                >
                  <AssignDepositPage />
                </ClientLayout>
              }
            ></Route>

            {/* Cobrar y pagar (pasamanos) — crea una cobranza y un pago a
                proveedor de una vez. Se gatea por COLLECTION_DEPOSITS, que es
                la pata que puede fallar y anularse sola; quien no puede cargar
                un cobro tampoco tiene por qué disparar el par. */}
            <Route
              path="cobrar-y-pagar"
              element={
                <ClientLayout
                  resource={RESOURCES.COLLECTION_DEPOSITS}
                  action="modify"
                >
                  <PassThroughPage />
                </ClientLayout>
              }
            ></Route>
          </Route>

          {/* Cupones y ofertas */}
          <Route path="cupones">
            {/* Cupones index */}
            <Route
              index
              element={
                <ClientLayout resource={RESOURCES.COUPON_COUPONS} action="view">
                  <CouponsPage />
                </ClientLayout>
              }
            />

            {/* Crear cupon */}
            <Route
              path="crear"
              element={
                <ClientLayout
                  resource={RESOURCES.COUPON_COUPONS}
                  action="modify"
                >
                  <AddCouponsPage />
                </ClientLayout>
              }
            />

            {/* Editar cupon */}
            <Route
              path="editar/:id"
              element={
                <ClientLayout
                  resource={RESOURCES.COUPON_COUPONS}
                  action="modify"
                >
                  <EditCouponPage />
                </ClientLayout>
              }
            />

            {/* Ofertas */}
            <Route path="ofertas">
              <Route
                index
                element={
                  <ClientLayout resource={RESOURCES.OFFER} action="view">
                    <OffersPage />
                  </ClientLayout>
                }
              />

              {/* Crear oferta */}
              <Route
                path="crear"
                element={
                  <ClientLayout resource={RESOURCES.OFFER} action="modify">
                    <AddOfferPage />
                  </ClientLayout>
                }
              />

              {/* Editar oferta */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout resource={RESOURCES.OFFER} action="modify">
                    <EditOfferPage />
                  </ClientLayout>
                }
              />
            </Route>
          </Route>

          {/* Caja */}
          <Route path="caja">
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_CASHDESKS}
                  action="view"
                >
                  <CashdeskPage />
                </ClientLayout>
              }
            ></Route>

            {/* Caja por local */}
            <Route
              path="local/:warehouseId"
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_CASHDESKS}
                  action="view"
                >
                  <CashdeskByWarehousePage />
                </ClientLayout>
              }
            ></Route>

            {/* Caja por cajero (ADR-0009) — REPORTE, no la pantalla operativa */}
            <Route
              path="por-cajero"
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_CASHDESKS}
                  action="view"
                >
                  <CashdeskByCashierPage />
                </ClientLayout>
              }
            ></Route>

            {/* Cajas cerradas */}
            <Route
              path="cajas-cerradas"
              element={
                <ClientLayout
                  resource={RESOURCES.COLLECTION_DEPOSITS}
                  action="modify"
                >
                  <ClosedCashdesksPage />
                </ClientLayout>
              }
            ></Route>

            {/* Gastos */}
            <Route path="gastos">
              <Route
                index
                element={
                  <ClientLayout
                    resource={RESOURCES.TREASURY_EXPENSES}
                    action="view"
                  >
                    <ExpensesPage />
                  </ClientLayout>
                }
              ></Route>

              {/* Agregar gasto */}
              <Route
                path="agregar"
                element={
                  <ClientLayout
                    resource={RESOURCES.TREASURY_EXPENSES}
                    action="modify"
                  >
                    <AddExpensePage />
                  </ClientLayout>
                }
              ></Route>

              {/* Editar gasto */}
              <Route
                path="editar/:id"
                element={
                  <ClientLayout
                    resource={RESOURCES.TREASURY_EXPENSES}
                    action="modify"
                  >
                    <EditExpensePage />
                  </ClientLayout>
                }
              ></Route>
            </Route>

            {/* Adelanto de comisiones */}
            <Route
              path="adelanto-de-comisiones"
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_EXPENSES}
                  action="view"
                >
                  <CommissionsPage />
                </ClientLayout>
              }
            ></Route>

            {/* Cheques */}
            <Route
              path="cheques"
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_CHECKS}
                  action="view"
                >
                  <ChecksPage />
                </ClientLayout>
              }
            ></Route>

            {/* Cartera de cheques (reporte). Reusa el recurso de cheques: son
                los mismos datos agregados, así que quien puede ver el listado
                puede ver el reporte — y quien no, tampoco por esta vía.
                Es el MISMO string que gatea el endpoint en el BFF
                (`require_authz(Resource::TreasuryChecks)`), así que el menú no
                puede ofrecer lo que el borde después niega. */}
            <Route
              path="cartera-de-cheques"
              element={
                <ClientLayout
                  resource={RESOURCES.TREASURY_CHECKS}
                  action="view"
                >
                  <ChecksPortfolioPage />
                </ClientLayout>
              }
            ></Route>
          </Route>

          {/* Ventas B2B (BigSupplier Seller) */}
          <Route path="ventas-b2b" element={<BigSupplierGuard />}>
            <Route
              index
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <PublishedProductsPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="productos"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <PublishedProductsPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="solicitudes"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <MyRequestsPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="pagos"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <MyPaymentRequestsPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="ordenes"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <MyOrdersPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="clientes"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <MyClientsPage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="aumentos"
              element={
                <ClientLayout
                  resource={RESOURCES.BIG_SUPPLIER_SELLER}
                  action="view"
                >
                  <MyPriceUpdatesPage />
                </ClientLayout>
              }
            ></Route>
          </Route>

          {/* Mi Tienda Online */}
          <Route path="mi-tienda-online">
            <Route
              index
              element={
                <ClientLayout pathname="/mi-tienda-online">
                  <MiTiendaOnlinePage />
                </ClientLayout>
              }
            ></Route>
            <Route
              path="crear"
              element={
                <ClientLayout pathname="/mi-tienda-online">
                  <CreateStorePage />
                </ClientLayout>
              }
            ></Route>
            <Route path=":storeId">
              <Route
                index
                element={
                  <ClientLayout pathname="/mi-tienda-online">
                    <StoreDashboardPage />
                  </ClientLayout>
                }
              ></Route>
              <Route
                path="apariencia"
                element={
                  <ClientLayout pathname="/mi-tienda-online">
                    <AparienciaPage />
                  </ClientLayout>
                }
              ></Route>
              <Route
                path="informacion"
                element={
                  <ClientLayout pathname="/mi-tienda-online">
                    <InformacionPage />
                  </ClientLayout>
                }
              ></Route>
              <Route
                path="envios"
                element={
                  <ClientLayout pathname="/mi-tienda-online">
                    <EnviosPage />
                  </ClientLayout>
                }
              ></Route>
              <Route
                path="pagos"
                element={
                  <ClientLayout pathname="/mi-tienda-online">
                    <PagosPage />
                  </ClientLayout>
                }
              ></Route>
            </Route>
          </Route>

          {/* Configuración */}
          <Route path="configuracion">
            <Route
              index
              element={
                <ClientLayout pathname="/">
                  <ConfigurationPage />
                </ClientLayout>
              }
            ></Route>
            <Route path="integraciones">
              <Route
                path="mercado-pago"
                element={
                  <ClientLayout
                    pathname="owner"
                    planFeature="integrations/mercadopago"
                  >
                    <MercadoPagoPage />
                  </ClientLayout>
                }
              />
              <Route
                path="mercado-libre"
                element={
                  <ClientLayout pathname="owner">
                    <MercadoLibrePage />
                  </ClientLayout>
                }
              />
            </Route>
          </Route>

          {/* Notificaciones */}
          <Route
            path="notificaciones"
            element={
              <ClientLayout pathname="/">
                <NotificationsPage />
              </ClientLayout>
            }
          />

          {/*
            Monitor del negocio (#205), gateado con `report/monitor`
            (trd-client#245).

            Es el recurso propio del monitor —no `report/reports`, que es el de
            los reportes de la Home—: los dos los define el módulo vendible
            `report` (trd-platform#112) y los proyecta billing. Desde trd-bff
            v3.49.0 el borde gatea `/api/v1/monitor/*` con este mismo id
            (trd-bff#284).

            `resolveModuleAccess` es fail-closed, incluso para el dueño: un
            negocio sin el módulo en `modules` no ve el monitor, aunque el borde
            al dueño lo deje pasar. Es lo laudado: el módulo se vende.

            Y es SÓLO del dueño (trd-client#285): `ownerOnly` se suma al
            recurso, así que el grant `report/monitor` de un empleado ya no abre
            la pantalla. El ítem del menú declara lo mismo.
          */}
          <Route
            path="monitor"
            element={
              <ClientLayout resource={RESOURCES.REPORT_MONITOR} ownerOnly>
                <MonitorPage />
              </ClientLayout>
            }
          />

          {/* Mi Suscripción */}
          <Route
            path="mi-suscripcion"
            element={
              <ClientLayout pathname="owner" ownerOnly>
                <SubscriptionPage />
              </ClientLayout>
            }
          />

          {/* Redirect from old /planes route */}
          <Route
            path="planes"
            element={
              <ClientLayout pathname="owner" ownerOnly>
                <SubscriptionPage />
              </ClientLayout>
            }
          />

          {/* Tableros */}
          <Route path="tableros">
            {/* Tableros index */}
            <Route
              index
              element={
                <ClientLayout resource={RESOURCES.BOARDS} action="view">
                  <BoardsPage />
                </ClientLayout>
              }
            />

            {/* Tablero individual */}
            <Route
              path=":id"
              element={
                <ClientLayout resource={RESOURCES.BOARDS} action="view">
                  <BoardPage />
                </ClientLayout>
              }
            />
          </Route>

          {/* Big Supplier Catálogo */}
          <Route
            path="big-supplier/:supplierId/catalogo"
            element={
              <ClientLayout
                resource={RESOURCES.SUPPLIER_SUPPLIERS}
                action="view"
              >
                <BigSupplierCatalogPage />
              </ClientLayout>
            }
          />

          {/* Big Supplier Pending Price Update */}
          <Route
            path="big-supplier/pending-price-update/:id"
            element={
              <ClientLayout
                resource={RESOURCES.SUPPLIER_SUPPLIERS}
                action="view"
              >
                <PendingPriceUpdatePage />
              </ClientLayout>
            }
          />

          {/* 404 */}
          <Route
            path="*"
            element={
              <ClientLayout
                resource={RESOURCES.SUPPLIER_BRANDS}
                action="modify"
              >
                <NotFoundPage />
              </ClientLayout>
            }
          />
        </Routes>
      </Suspense>
    </QueryClientProvider>
  );
}

export default AppRoutes;
